/**
 * "The most you can enter" for the order size, perp or spot, from the P2 layer.
 *
 * Two answers from one resolver, because they answer different questions:
 *
 *   - `sizeMax` — what MAX, the % chips and the slider fill.
 *       perp: `perpOrderMax` holding back the 0.5% buffer on EVERY order
 *       (decision 5), so fees and mark drift cannot tip a MAX order over.
 *       spot: `spotOrderMax` — the base balance for a SELL, the quote balance
 *       for a BUY; a market buy on a chain with a `maxQuoteIn` cap leaves the
 *       slippage headroom so the cap never exceeds what the trader holds.
 *   - `sizeCap` — the validator's ceiling: what the exchange would accept.
 *       perp: the buffer on market orders only, as always; spot: no headroom.
 *
 * Perp maxima include the opposite position (a flip is one order, decision 8),
 * are capped by the chain's `maxOrderSize` and floored onto the chain's grid
 * (`useMarketContractSpecs`). Spot uses the market's API grid.
 */
import { useContext, useMemo } from "react";
import { OrderTypeResponse, SideResponse } from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { SettingsContext } from "../../../contexts/SettingsContext";
import { useMarketContractSpecs } from "../../../hooks/contracts/useMarketContractSpecs";
import { Dec, dec } from "../../../utils/amount/decimal";
import { MaxResult, MaxUnavailableReason } from "../../../utils/amount/max/types";
import { PerpOrderMaxInput, perpOrderMax } from "../../../utils/amount/max/perpOrderMax";
import { spotOrderMax } from "../../../utils/amount/max/spotOrderMax";
import { MarketSteps, marketSteps } from "../../../utils/amount/spec";
import { getAssetShortName } from "../../../utils/asset/assets";
import { AssetResponse } from "../../../utils/asset";
import { MARKET_ORDER_MARGIN_BUFFER_BPS } from "./PerpsTradeForm.helpers";
import { usePerpsTradeFormNotional } from "./usePerpsTradeFormNotional";

/** A quote-terms size is money: cents. */
export const QUOTE_SIZE_STEP: Dec = "0.01";

const positiveText = (value: unknown): Dec | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = dec.parse(String(value));
  return parsed !== null && dec.gt(parsed, "0") ? parsed : undefined;
};

/**
 * The resolver's input from the form's state. Pure, so the mapping — which is
 * where a unit or a sign goes wrong — is testable without rendering the form.
 */
export const buildPerpMaxInput = ({
  side,
  orderType,
  useQuoteTerms,
  withdrawable,
  availableBalance,
  leverage,
  feeRate,
  limitPrice,
  book,
  mark,
  steps,
  position,
  positionsKnown = true,
  reduceOnly,
  baseSymbol,
}: {
  side: SideResponse;
  orderType: OrderTypeResponse;
  useQuoteTerms: boolean;
  withdrawable: string | null | undefined;
  /** Used only when the API omits `withdrawable` altogether (older backends). */
  availableBalance?: string | null;
  leverage: number;
  feeRate: number | undefined;
  limitPrice?: string;
  book?: { bids?: string[][]; asks?: string[][] };
  mark?: number | string;
  steps: MarketSteps;
  position?: { side?: string; amount?: string | number };
  /** False while positions are loading or their read failed: no row is not "flat". */
  positionsKnown?: boolean;
  reduceOnly: boolean;
  baseSymbol?: string;
}): PerpOrderMaxInput => ({
  side: side === SideResponse.Sell ? "sell" : "buy",
  orderType: orderType === OrderTypeResponse.Market ? "market" : "limit",
  unit: useQuoteTerms ? "quote" : "base",
  // `null` is the API's "cannot value this account" and must stay unknown. An
  // API that omits the field predates it: fall back to available_balance, as
  // buying power always has (computeBuyingPower) — the same figure "Available
  // to Trade" shows.
  withdrawable:
    withdrawable === null
      ? null
      : withdrawable === undefined
      ? availableBalance === null || availableBalance === undefined
        ? null
        : String(availableBalance)
      : String(withdrawable),
  leverage: positiveText(leverage),
  feeRate:
    feeRate === undefined || !Number.isFinite(feeRate) ? null : dec.parse(feeRate) ?? null,
  limitPrice: positiveText(limitPrice),
  book,
  mark: positiveText(mark),
  steps,
  position:
    position?.side && position.amount !== undefined
      ? {
          side: position.side === SideResponse.Sell ? "sell" : "buy",
          amount: String(position.amount),
        }
      : undefined,
  positionsKnown,
  reduceOnly,
  baseSymbol,
});

/**
 * Does `text` fit under the validator's ceiling?
 *
 * Only a KNOWN shortfall fails: a size over a known ceiling, or a balance the
 * API reports as zero. A balance, withdrawable or fee the API has not given us
 * is unknown, not insufficient — this rule used to answer it with "Insufficient
 * buying power", the unknown-is-zero bug in validator form (B-1 / A17). MAX
 * still says why it cannot fill, and the chain re-checks the order. An unknown
 * grid or price never blocked. (A perp size the opposite position covers is
 * allowed before this is asked — see the Sizing validator.)
 */
/** What the balance check needs and the API has not given: nothing to compare. */
const BALANCE_INPUT_UNKNOWN: ReadonlySet<string> = new Set([
  "balance_unknown",
  "withdrawable_unknown",
  "fee_unknown",
]);

const UNKNOWN_IS_NOT_INSUFFICIENT: ReadonlySet<string> = new Set([
  "specs_unknown",
  "price_unknown",
  ...Array.from(BALANCE_INPUT_UNKNOWN),
]);

/**
 * The reason a size CANNOT be checked against a balance yet, or `undefined`.
 * The neutral middle state: not "insufficient" (sizeWithinCap passes it), but
 * the order waits — submit stays disabled and the form shows the MAX
 * unavailable text instead of an error.
 */
export const sizeBalanceUnknownReason = (
  text: string | undefined,
  cap: MaxResult
): MaxUnavailableReason | undefined => {
  const value = dec.parse(text ?? "");
  if (value === null || !dec.gt(value, "0")) return undefined; // nothing entered
  if (cap.ok || !BALANCE_INPUT_UNKNOWN.has(cap.reason)) return undefined;
  return cap.reason;
};

export const sizeWithinCap = (text: string, cap: MaxResult): boolean => {
  const value = dec.parse(text);
  if (value === null || !dec.gt(value, "0")) return true; // other rules own empty/zero
  if (cap.ok) return dec.lte(value, cap.max);
  return UNKNOWN_IS_NOT_INSUFFICIENT.has(cap.reason);
};

export function usePerpsTradeFormMax(
  ctx: ReturnType<typeof usePerpsTradeFormNotional>
) {
  const {
    isPerp,
    isSpot,
    perpInstrument,
    accountData,
    selectedLeverage,
    openingFeeRate,
    orderDirection,
    orderType,
    tradeUseUSDCTerms,
    price,
    orderbookData,
    markPrice,
    currentPosition,
    positionsKnown,
    reduceOnly,
    spotTokenBalances,
  } = ctx;
  const { slippageBps } = useContext(SettingsContext);

  // The chain's own grid and ceiling. Perp only: spot sizes come from its pool.
  const { specs } = useMarketContractSpecs(isPerp ? perpInstrument?.instrument_id : undefined);
  const sizeSteps = useMemo(
    () => marketSteps(perpInstrument, isPerp ? specs : undefined),
    [isPerp, perpInstrument, specs]
  );
  const baseSymbol =
    getAssetShortName(perpInstrument?.underlying_asset as AssetResponse) ||
    perpInstrument?.underlying_asset;
  const book = useMemo(
    () =>
      orderbookData
        ? { bids: orderbookData.bids, asks: orderbookData.asks }
        : undefined,
    [orderbookData]
  );

  const perpInput = useMemo(
    () =>
      buildPerpMaxInput({
        side: orderDirection,
        orderType,
        useQuoteTerms: Boolean(tradeUseUSDCTerms),
        withdrawable: accountData?.withdrawable,
        availableBalance: accountData?.available_balance,
        leverage: selectedLeverage,
        feeRate: openingFeeRate,
        limitPrice: price,
        book,
        mark: markPrice,
        steps: sizeSteps,
        position: currentPosition,
        positionsKnown,
        reduceOnly: Boolean(reduceOnly),
        baseSymbol,
      }),
    [
      accountData?.available_balance,
      accountData?.withdrawable,
      baseSymbol,
      book,
      currentPosition,
      markPrice,
      openingFeeRate,
      orderDirection,
      orderType,
      positionsKnown,
      price,
      reduceOnly,
      selectedLeverage,
      sizeSteps,
      tradeUseUSDCTerms,
    ]
  );

  const spotInput = useMemo(() => {
    const config = getCurrentChainConfig();
    return {
      side: orderDirection === SideResponse.Sell ? ("sell" as const) : ("buy" as const),
      orderType:
        orderType === OrderTypeResponse.Market ? ("market" as const) : ("limit" as const),
      unit: tradeUseUSDCTerms ? ("quote" as const) : ("base" as const),
      // `null` = not known yet: MAX says wait, it never offers 0.
      baseAvailable: spotTokenBalances?.base.amount ?? null,
      quoteAvailable: spotTokenBalances?.quote.amount ?? null,
      limitPrice: positiveText(price),
      book,
      mark: positiveText(markPrice),
      steps: sizeSteps,
      // The same routers the order path sends a `maxQuoteIn` to.
      hasMaxQuoteIn: config.spotMarketMaxQuoteIn === true || config.chainId === 10143,
      baseSymbol,
      quoteSymbol: "USDC",
    };
  }, [
    baseSymbol,
    book,
    markPrice,
    orderDirection,
    orderType,
    price,
    sizeSteps,
    spotTokenBalances,
    tradeUseUSDCTerms,
  ]);

  const sizeMax: MaxResult = useMemo(
    () =>
      isSpot
        ? spotOrderMax({ ...spotInput, slippageBps })
        : perpOrderMax({ ...perpInput, bufferBps: MARKET_ORDER_MARGIN_BUFFER_BPS }),
    [isSpot, perpInput, slippageBps, spotInput]
  );
  // Perp: buying power, not the reduce-only limit — an over-sized reduce-only
  // order has its own message, which this must not pre-empt. Spot: the balance
  // with no slippage headroom — what the exchange itself would accept.
  const sizeCap: MaxResult = useMemo(
    () =>
      isSpot
        ? spotOrderMax({ ...spotInput, slippageBps: 0 })
        : perpOrderMax({ ...perpInput, reduceOnly: false }),
    [isSpot, perpInput, spotInput]
  );

  // The step a chip or the slider writes on: the grid in base terms, cents in USD.
  const sizeStep: Dec = tradeUseUSDCTerms ? QUOTE_SIZE_STEP : sizeSteps.amountStep;

  return { ...ctx, sizeSteps, sizeMax, sizeCap, sizeStep };
}
